/**
 * MfaGate.tsx — the challenge screen that makes enrolled MFA usable.
 *
 * THE PROBLEM IT SOLVES
 * ---------------------
 * Supabase issues `aal1` on password sign-in. A second factor raises the session to
 * `aal2` only when the user answers a TOTP challenge — and nothing in this admin ever
 * asked for one. Meanwhile the restrictive RLS policy on all 23 admin-gated tables reads
 * that claim:
 *
 *   non-admin -> never gated
 *   admin, no verified factor -> aal1 accepted
 *   admin, verified factor -> aal2 required
 *
 * So the first login after enrolling would have landed an editor in an admin where every
 * queue renders EMPTY. Not an error — RLS denial is silent, rows simply do not come back.
 * The operator's reasonable conclusion is "the database is broken", and the actual fix
 * (visit Security, type six digits) is nowhere on screen. Shipping enrolment without this
 * screen would have manufactured an outage out of a security improvement.
 *
 * WHY THIS FAILS OPEN, DELIBERATELY
 * ---------------------------------
 * This component is CONVENIENCE, not enforcement. The enforcement is the RLS policy, and
 * it is in the database where a browser cannot reach it. So if the AAL check itself fails
 * — offline, auth hiccup, unexpected shape — this renders the app rather than blocking it.
 * Blocking would take the admin down over a failed *diagnostic*, while gaining nothing:
 * an un-stepped-up session still cannot read a single gated row. The failure is surfaced
 * as a banner instead of swallowed, so a persistent problem is visible.
 *
 * This is the opposite of the rule SystemHealth.tsx follows, and the difference is worth
 * stating: there, an unknown state must never look like a healthy one, because the page's
 * whole job is to report state. Here the page's job is to unblock a person, and the thing
 * being protected is protected somewhere else.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2, AlertTriangle, LogOut } from 'lucide-react';
import { getClient } from '../lib/supabase';
import { useEnv } from '../lib/env';
import { useAuth } from '../lib/auth';

const CODE_LEN = 6;

type GateState =
  /** Still asking. Render nothing rather than flashing either outcome. */
  | { kind: 'checking' }
  /** No verified factor, or already aal2. Nothing to do. */
  | { kind: 'clear' }
  /** A verified factor exists and this session has not answered a challenge. */
  | { kind: 'challenge'; factorId: string }
  /** We could not determine the level. Render the app; say so. */
  | { kind: 'unknown'; reason: string };

export function MfaGate({ children }: { children: React.ReactNode }) {
  // The challenge has to run against the project that issued the session, and
  // that is now whichever env the route names. Reading Proto's factor list while
  // sitting on a Stage route would report "nothing to challenge" and drop the
  // operator into an admin whose every gated table reads empty — the exact
  // manufactured outage the header of this file exists to prevent.
  const env = useEnv();
  const supabase = getClient(env);
  const { user, logout } = useAuth();
  const [state, setState] = useState<GateState>({ kind: 'checking' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const check = useCallback(async () => {
    if (!supabase || !user) { setState({ kind: 'clear' }); return; }
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) { setState({ kind: 'unknown', reason: error.message }); return; }

      // nextLevel === 'aal2' means a verified factor exists for this user. currentLevel
      // is what this session actually holds. The gate is the gap between them.
      if (data?.currentLevel === 'aal2' || data?.nextLevel !== 'aal2') {
        setState({ kind: 'clear' });
        return;
      }

      const { data: factors, error: fErr } = await supabase.auth.mfa.listFactors();
      if (fErr) { setState({ kind: 'unknown', reason: fErr.message }); return; }
      const totp = factors?.totp?.[0];
      if (!totp) {
        // nextLevel said aal2 but no verified TOTP came back. Contradictory; do not guess.
        setState({ kind: 'unknown', reason: 'assurance level expects a factor, but none was listed' });
        return;
      }
      setState({ kind: 'challenge', factorId: totp.id });
    } catch (e) {
      setState({ kind: 'unknown', reason: (e as Error).message });
    }
  }, [user, supabase]);

  useEffect(() => { void check(); }, [check]);

  async function submit() {
    if (!supabase || state.kind !== 'challenge') return;
    if (code.trim().length !== CODE_LEN) {
      setProblem(`Enter the ${CODE_LEN}-digit code from your authenticator app.`);
      return;
    }
    setBusy(true); setProblem(null);
    const ch = await supabase.auth.mfa.challenge({ factorId: state.factorId });
    if (ch.error || !ch.data) {
      setBusy(false);
      setProblem(`Could not start the challenge: ${ch.error?.message ?? 'no reason given'}`);
      return;
    }
    const v = await supabase.auth.mfa.verify({
      factorId: state.factorId,
      challengeId: ch.data.id,
      code: code.trim(),
    });
    setBusy(false);
    if (v.error) { setProblem(`That code was not accepted: ${v.error.message}`); return; }
    setCode('');
    await check();
  }

  if (state.kind === 'checking') return null;

  if (state.kind === 'challenge') {
    return (
      <div className="fixed inset-0 z-50 bg-surface-light flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white border border-stone-200 rounded-lg p-6 space-y-4">
          <div>
            <h1 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
              <ShieldCheck size={18} className="text-stone-500" /> Second factor required
            </h1>
            <p className="text-sm text-stone-600 mt-1">
              You have an authenticator enrolled, so this session needs a code before the editorial
              tables will open. Without it every queue reads empty — that is the security policy
              working, not a fault.
            </p>
          </div>

          <input
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LEN))}
            onKeyDown={e => { if (e.key === 'Enter') void submit(); }}
            placeholder="000000"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            disabled={busy}
            className="w-44 font-mono tracking-[0.3em] text-lg px-3 py-2 border border-stone-300 rounded-md disabled:opacity-50"
          />

          {problem && (
            <p className="text-sm text-red-700 flex gap-2">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> <span>{problem}</span>
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => void submit()}
              disabled={busy || code.trim().length !== CODE_LEN}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-stone-900 text-white rounded-md disabled:opacity-40"
            >
              {busy && <Loader2 size={14} className="animate-spin" />} Continue
            </button>
            {/* Lost your device? Signing out is the honest exit — there is no bypass here,
                and pretending otherwise would be worse than a dead end. */}
            <button
              onClick={() => void logout()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-stone-300 rounded-md hover:bg-stone-50"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>

          <p className="text-xs text-stone-500">
            Lost your authenticator? There is no bypass from this screen — the factor has to be
            removed server-side before you can sign in without it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {state.kind === 'unknown' && (
        <div className="bg-amber-50 border-b border-amber-300 px-4 py-2 text-xs text-amber-900">
          <span className="font-semibold">Could not confirm this session's assurance level</span>
          {' '}({state.reason}). The admin is still usable and your data is still protected by the
          database policy — but if gated tables read empty, that is why. Go to Security to enter a
          code.
        </div>
      )}
      {children}
    </>
  );
}
